import React from 'react';
import { Target, TrendingDown, Package, FileText, CheckCircle } from 'lucide-react';

interface SessionPlanReviewProps {
  clientName: string;
  sessionPlan?: {
    programs: Array<{
      programId: string;
      programName: string;
      targetGoal: string;
      trialsPlanned: number;
    }>;
    behaviorTargets?: string[];
    materials?: string[];
    notes?: string;
  };
  onContinue: () => void;
}

export const SessionPlanReview: React.FC<SessionPlanReviewProps> = ({
  clientName,
  sessionPlan,
  onContinue,
}) => {
  const hasSessionPlan = sessionPlan && (
    (sessionPlan.programs && sessionPlan.programs.length > 0) ||
    (sessionPlan.behaviorTargets && sessionPlan.behaviorTargets.length > 0) ||
    (sessionPlan.materials && sessionPlan.materials.length > 0)
  );

  // Inline styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '24px',
    },
    headerBox: {
      backgroundColor: '#f0fdfa',
      border: '1px solid #99f6e4',
      borderRadius: '8px',
      padding: '16px',
    },
    headerContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    headerIcon: {
      width: '24px',
      height: '24px',
      color: '#3d4d50',
      marginTop: '2px',
      flexShrink: 0,
    },
    headerTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#134e4a',
      margin: 0,
    },
    headerSubtitle: {
      fontSize: '14px',
      color: '#4F6367',
      marginTop: '4px',
      margin: 0,
    },
    headerHint: {
      fontSize: '12px',
      color: '#3d4d50',
      marginTop: '8px',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '12px',
      margin: 0,
    },
    programCard: {
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
    },
    programHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: '8px',
    },
    programBadge: {
      fontSize: '12px',
      padding: '2px 8px',
      backgroundColor: '#ccfbf1',
      color: '#4F6367',
      borderRadius: '9999px',
      fontWeight: 500,
      marginRight: '8px',
    },
    programName: {
      fontSize: '14px',
      fontWeight: 700,
      color: '#2d2d2d',
    },
    programGoal: {
      fontSize: '12px',
      color: '#6b6b6b',
      marginTop: '8px',
    },
    trialsContainer: {
      textAlign: 'right' as const,
    },
    trialsLabel: {
      fontSize: '12px',
      color: '#6b6b6b',
    },
    trialsValue: {
      fontSize: '24px',
      fontWeight: 700,
      color: '#3d4d50',
    },
    listContainer: {
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      padding: '16px',
    },
    listItem: {
      fontSize: '14px',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      marginBottom: '8px',
    },
    listIcon: {
      width: '16px',
      height: '16px',
      color: '#f97316',
      marginTop: '2px',
      flexShrink: 0,
    },
    materialsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
    },
    materialItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: '#374151',
      backgroundColor: '#f9fafb',
      padding: '8px 12px',
      borderRadius: '4px',
    },
    materialIcon: {
      width: '12px',
      height: '12px',
      color: '#6b6b6b',
    },
    notesBox: {
      backgroundColor: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '8px',
      padding: '16px',
    },
    notesText: {
      fontSize: '14px',
      color: '#1e3a8a',
      margin: 0,
    },
    noplanBox: {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e0db',
      borderRadius: '8px',
      padding: '24px',
      textAlign: 'center' as const,
    },
    noplanIcon: {
      width: '48px',
      height: '48px',
      color: '#9ca3af',
      margin: '0 auto 12px',
    },
    noplanTitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '4px',
    },
    noplanText: {
      fontSize: '12px',
      color: '#6b6b6b',
    },
    summaryBox: {
      backgroundColor: '#f0fdfa',
      border: '1px solid #99f6e4',
      borderRadius: '8px',
      padding: '16px',
    },
    summaryTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#134e4a',
      marginBottom: '8px',
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '16px',
      textAlign: 'center' as const,
    },
    summaryValue: {
      fontSize: '24px',
      fontWeight: 700,
      color: '#3d4d50',
    },
    summaryLabel: {
      fontSize: '12px',
      color: '#4F6367',
    },
    continueButton: {
      width: '100%',
      padding: '12px 24px',
      backgroundColor: '#3d4d50',
      color: '#ffffff',
      borderRadius: '8px',
      border: 'none',
      fontWeight: 500,
      fontSize: '16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s',
    },
    infoList: {
      fontSize: '12px',
      color: '#6b6b6b',
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    infoItem: {
      marginBottom: '4px',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.headerBox}>
        <div style={styles.headerContent}>
          <Target style={styles.headerIcon} />
          <div>
            <h3 style={styles.headerTitle}>Today's Session Plan</h3>
            <p style={styles.headerSubtitle}>
              Review planned programs and goals for {clientName}
            </p>
            {!hasSessionPlan && (
              <p style={styles.headerHint}>
                No structured plan available - proceed with standard programming
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Session Plan Content */}
      {hasSessionPlan ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Programs */}
          {sessionPlan.programs && sessionPlan.programs.length > 0 && (
            <div>
              <h4 style={styles.sectionTitle}>
                <Target style={{ width: '16px', height: '16px' }} />
                Programs ({sessionPlan.programs.length})
              </h4>
              <div>
                {sessionPlan.programs.map((program, idx) => (
                  <div key={program.programId} style={styles.programCard}>
                    <div style={styles.programHeader}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={styles.programBadge}>Program {idx + 1}</span>
                          <h5 style={styles.programName}>{program.programName}</h5>
                        </div>
                        <p style={styles.programGoal}>
                          <span style={{ fontWeight: 500 }}>Target Goal:</span> {program.targetGoal}
                        </p>
                      </div>
                      <div style={styles.trialsContainer}>
                        <div style={styles.trialsLabel}>Trials Planned</div>
                        <div style={styles.trialsValue}>{program.trialsPlanned}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Behavior Targets */}
          {sessionPlan.behaviorTargets && sessionPlan.behaviorTargets.length > 0 && (
            <div>
              <h4 style={styles.sectionTitle}>
                <TrendingDown style={{ width: '16px', height: '16px' }} />
                Behavior Targets
              </h4>
              <div style={styles.listContainer}>
                {sessionPlan.behaviorTargets.map((behavior, idx) => (
                  <div key={idx} style={styles.listItem}>
                    <CheckCircle style={styles.listIcon} />
                    {behavior}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Materials Needed */}
          {sessionPlan.materials && sessionPlan.materials.length > 0 && (
            <div>
              <h4 style={styles.sectionTitle}>
                <Package style={{ width: '16px', height: '16px' }} />
                Materials Needed
              </h4>
              <div style={styles.listContainer}>
                <div style={styles.materialsGrid}>
                  {sessionPlan.materials.map((material, idx) => (
                    <div key={idx} style={styles.materialItem}>
                      <Package style={styles.materialIcon} />
                      {material}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Additional Notes */}
          {sessionPlan.notes && (
            <div>
              <h4 style={styles.sectionTitle}>
                <FileText style={{ width: '16px', height: '16px' }} />
                Session Notes
              </h4>
              <div style={styles.notesBox}>
                <p style={styles.notesText}>{sessionPlan.notes}</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* No Session Plan */
        <div style={styles.noplanBox}>
          <FileText style={styles.noplanIcon} />
          <p style={styles.noplanTitle}>No Structured Session Plan</p>
          <p style={styles.noplanText}>
            Use your standard programs and clinical judgment for this session
          </p>
        </div>
      )}

      {/* Summary Box */}
      {hasSessionPlan && (
        <div style={styles.summaryBox}>
          <h4 style={styles.summaryTitle}>Session Summary</h4>
          <div style={styles.summaryGrid}>
            <div>
              <div style={styles.summaryValue}>{sessionPlan.programs?.length || 0}</div>
              <div style={styles.summaryLabel}>Programs</div>
            </div>
            <div>
              <div style={styles.summaryValue}>
                {sessionPlan.programs?.reduce((sum, p) => sum + p.trialsPlanned, 0) || 0}
              </div>
              <div style={styles.summaryLabel}>Total Trials</div>
            </div>
            <div>
              <div style={styles.summaryValue}>{sessionPlan.behaviorTargets?.length || 0}</div>
              <div style={styles.summaryLabel}>Behaviors</div>
            </div>
          </div>
        </div>
      )}

      {/* Continue Button */}
      <button
        onClick={onContinue}
        style={styles.continueButton}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4F6367')}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#3d4d50')}
      >
        <CheckCircle style={{ width: '20px', height: '20px' }} />
        I've Reviewed the Plan - Continue
      </button>

      {/* Info */}
      <ul style={styles.infoList}>
        <li style={styles.infoItem}>• Review all programs and goals before starting the session</li>
        <li style={styles.infoItem}>• Gather all required materials listed above</li>
        <li style={styles.infoItem}>• Follow behavior intervention strategies as planned</li>
        <li style={styles.infoItem}>• Document actual performance vs. planned trials</li>
      </ul>
    </div>
  );
};
